import {Box, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import type {Resolver} from "../../../../../../sdo/Resolver.ts";

// --- Shared styles ---

export const fieldSx = {
    backgroundColor: "rgba(9, 25, 32, 0.6)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        color: "#f2f5fa",
        fontSize: "0.75rem",
        height: "38px",
        "& fieldset": {border: "1px solid rgba(112, 147, 160, 0.35)"},
        "&:hover fieldset": {border: "1px solid #02cfee"},
        "&.Mui-focused fieldset": {border: "1px solid #38ff82", boxShadow: "0 0 6px rgba(56, 255, 130, 0.2)"},
    },
    "& .MuiInputLabel-root": {color: "rgba(242, 245, 250, 0.55)", fontSize: "0.75rem"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#38ff82"},
    "& input::placeholder": {color: "rgba(242, 245, 250, 0.35)", opacity: 1},
};

export const selectSx = {
    ...fieldSx,
    "& .MuiSelect-select": {color: "#f2f5fa", fontSize: "0.75rem", textAlign: "left" as const},
    "& .MuiSelect-icon": {color: "#7093a0"},
};

export const toggleGroupSx = {
    "& .MuiToggleButton-root": {
        color: "#eaeaea",
        fontSize: "0.65rem",
        fontWeight: 400,
        textTransform: "none" as const,
        padding: "4px 12px",
        border: "1px solid rgba(112, 147, 160, 0.35)",
        height: "32px",
    },
    "& .MuiToggleButton-root.Mui-selected": {
        color: "#091920",
        fontWeight: 600,
        backgroundColor: "#02cfee",
        borderColor: "#02cfee",
    },
    "& .MuiToggleButton-root.Mui-selected:hover": {
        backgroundColor: "rgba(2, 207, 238, 0.85)",
    },
};

export const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {color: "#02cfee"},
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: "#02cfee"},
};

export const labelSx = {color: "#02cfee", fontSize: "0.70rem", whiteSpace: "nowrap" as const};
export const hintSx = {color: "rgba(242, 245, 250, 0.35)", fontSize: "0.60rem", fontStyle: "italic"};
export const colHeaderSx = {color: "rgba(242, 245, 250, 0.45)", fontSize: "0.60rem", textAlign: "center" as const};

export const rowSx = {
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap" as const,
    gap: "8px",
    padding: "3px 0px",
};

// --- Summary styles & components ---

// Match JavaFX: .resolver-summary-label { color: #02cfee; font-size: 12 }
export const summaryLabelSx = {color: "#02cfee", fontSize: "0.75rem", whiteSpace: "nowrap" as const};
// Match JavaFX: .resolver-summary-value { color: #FFFFFF; font-size: 12; font-weight: 700 }
export const summaryValueSx = {color: "#ffffff", fontSize: "0.75rem", fontWeight: 700};

export function SummaryRow({label, value}: { label: string; value: string | number }) {
    return (
        <Box sx={{display: "flex", gap: 0.5, alignItems: "baseline"}}>
            <Typography sx={summaryLabelSx}>{label}:</Typography>
            <Typography sx={summaryValueSx}>{String(value)}</Typography>
        </Box>
    );
}

export function SummaryGrid({children}: { children: React.ReactNode }) {
    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2px 24px",
            width: "100%",
            paddingTop: "4px",
        }}>
            {children}
        </Box>
    );
}

// --- Pill-style view mode components ---

export function MatchModePill({resolver}: { resolver: Resolver }) {
    const isExplicit = resolver.explicit;
    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "4px 14px",
            borderRadius: "10px",
            border: isExplicit ? "1px solid rgba(2, 207, 238, 0.6)" : "1px solid rgba(112, 147, 160, 0.35)",
            backgroundColor: "rgba(9, 25, 32, 0.6)",
            minWidth: "60px",
        }}>
            <Typography sx={{color: "rgba(242, 245, 250, 0.55)", fontSize: "0.65rem", lineHeight: 1.2}}>
                {isExplicit ? "Explicit:" : "Weighted:"}
            </Typography>
            <Typography sx={{color: "#ffffff", fontSize: "0.80rem", fontWeight: 700, lineHeight: 1.3}}>
                {resolver.weight}
            </Typography>
        </Box>
    );
}

export function SummaryPill({label, value}: { label: string; value: string | number }) {
    return (
        <Box sx={{
            display: "flex",
            gap: 0.75,
            alignItems: "center",
            padding: "6px 14px",
            borderRadius: "20px",
            border: "1px solid rgba(112, 147, 160, 0.35)",
            backgroundColor: "rgba(9, 25, 32, 0.6)",
        }}>
            <Typography sx={{color: "rgba(242, 245, 250, 0.55)", fontSize: "0.70rem", whiteSpace: "nowrap", textAlign: "left"}}>
                {label}:
            </Typography>
            <Typography sx={{color: "#ffffff", fontSize: "0.75rem", textAlign: "left"}}>
                {String(value)}
            </Typography>
        </Box>
    );
}

export function SummaryPillRow({children}: { children: React.ReactNode }) {
    return (
        <Stack direction="row" sx={{
            width: "100%",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            padding: "4px 0",
        }}>
            {children}
        </Stack>
    );
}

// --- Shared props interface ---

export interface ResolverEditorProps {
    draft: Resolver;
    editing: boolean;
    onChange: (updated: Resolver) => void;
}

// --- Helpers ---

export function set(draft: Resolver, key: string, value: unknown, onChange: (r: Resolver) => void) {
    onChange({...draft, [key]: value} as Resolver);
}

export function setNum(draft: Resolver, key: string, raw: string, onChange: (r: Resolver) => void) {
    const n = parseFloat(raw);
    onChange({...draft, [key]: isNaN(n) ? 0 : n} as Resolver);
}

// --- Reusable sub-components ---

export function MatchModeRow({draft, onChange}: ResolverEditorProps) {
    return (
        <Stack direction="row" sx={{...rowSx, padding: "6px 0"}}>
            <ToggleButtonGroup
                exclusive size="small"
                value={draft.explicit ? "EXPLICIT" : "WEIGHTED"}
                onChange={(_e, v) => {
                    if (!v) return;
                    if (v === "EXPLICIT") onChange({...draft, explicit: true, weight: 100} as Resolver);
                    else onChange({...draft, explicit: false} as Resolver);
                }}
                sx={toggleGroupSx}
            >
                <ToggleButton value="EXPLICIT">Explicit</ToggleButton>
                <ToggleButton value="WEIGHTED">Weighted</ToggleButton>
            </ToggleButtonGroup>
            <TextField
                size="small" type="number" value={draft.weight} label="Weight"
                disabled={draft.explicit}
                onChange={(e) => setNum(draft, "weight", e.target.value, onChange)}
                sx={{...fieldSx, width: "80px"}}
                inputProps={{min: 1, max: 100}}
            />
        </Stack>
    );
}

export function OperatorToggle({value, options, onChange}: {
    value: string;
    options: { label: string; value: string }[];
    onChange: (v: string) => void;
}) {
    return (
        <ToggleButtonGroup
            exclusive size="small" value={value}
            onChange={(_e, v) => { if (v) onChange(v); }}
            sx={toggleGroupSx}
        >
            {options.map((o) => (
                <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

export function F({value, placeholder, onChange, flex, width}: {
    value: string | number;
    placeholder: string;
    onChange: (v: string) => void;
    flex?: string;
    width?: string;
}) {
    return (
        <TextField
            size="small" value={value} label={placeholder}
            onChange={(e) => onChange(e.target.value)}
            sx={{...fieldSx, ...(flex ? {flex} : {}), ...(width ? {width} : {})}}
        />
    );
}

export function Sel({value, onChange, options, minWidth, label}: {
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    minWidth?: string;
    label?: string;
}) {
    return (
        <TextField
            size="small" select value={value} label={label}
            onChange={(e) => onChange(e.target.value)}
            sx={{...selectSx, minWidth: minWidth || "120px"}}
        >
            {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
        </TextField>
    );
}

export function Sw({checked, label, onChange}: { checked: boolean; label: string; onChange: (v: boolean) => void }) {
    return (
        <Stack direction="row" sx={{alignItems: "center", gap: "4px"}}>
            <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} sx={switchSx}/>
            <Typography sx={labelSx}>{label}</Typography>
        </Stack>
    );
}

export function DateF({value, placeholder, onChange, width}: {
    value: string; placeholder: string; onChange: (v: string) => void; width?: string;
}) {
    return (
        <TextField
            size="small" value={value} label={placeholder}
            onChange={(e) => onChange(e.target.value)}
            sx={{...fieldSx, width: width || "72px"}}
            inputProps={{style: {textAlign: "center"}}}
        />
    );
}
